import { z } from "zod";
import { isNotNil, mapValues } from "../../lib/functional";
import { StableValue } from "../../lib/stable-value";
import { safeParse } from "../../lib/zod";
import { Targets } from "../../introspection";
import { Color } from "../color";
import { ControlDefinition, serialize } from "../definition";
import { DefaultControlInstance } from "../instance";
import * as Schema from "./schema";
import { mergeStyles } from "./style";
class Definition extends ControlDefinition {
  static type = "makeswift::controls::typography";
  static get schema() {
    const type = z.literal(Definition.type);
    const data = Schema.typography(
      z.object({
        swatchId: z.string().nullable(),
        alpha: z.number().nullable()
      }).nullable()
    );
    const value = data;
    const resolvedValue = z.string();
    const definition = z.object({
      type
    });
    return {
      type,
      definition,
      data,
      value,
      resolvedValue
    };
  }
  static deserialize(data) {
    if (data.type !== Definition.type) {
      throw new Error(
        `Typography: expected type ${Definition.type}, got ${data.type}`
      );
    }
    Definition.schema.definition.parse(data);
    return new class Typography extends Definition {
    }();
  }
  constructor() {
    super({});
  }
  get controlType() {
    return Definition.type;
  }
  get schema() {
    return Definition.schema;
  }
  safeParse(data) {
    return safeParse(this.schema.data, data);
  }
  fromData(data) {
    return this.schema.data.optional().parse(data);
  }
  toData(value) {
    return value;
  }
  copyData(data, context) {
    if (data == null)
      return data;
    return {
      id: context.replacementContext.typographyIds.get(data.id ?? "") ?? data.id,
      style: data.style.map((override) => ({
        ...override,
        value: {
          ...override.value,
          ...override.value.color == null ? {} : {
            // Can't use Color().copyData() here as typography's color
            // definition accepts null for swatchId and alpha, while Color
            // Data does not.
            color: {
              ...override.value.color,
              alpha: override.value.color.alpha ?? null,
              swatchId: context.replacementContext.swatchIds.get(
                override.value.color.swatchId ?? ""
              ) ?? override.value.color.swatchId ?? null
            }
          }
        }
      }))
    };
  }
  resolveValue(data, resolver, stylesheet, _control) {
    const typographySub = resolver.resolveTypography(data?.id);
    const typographyData = StableValue({
      name: `${Definition.type}:data`,
      read: () => this.fragmentToData(typographySub.readStable()),
      deps: [typographySub]
    });
    const overrideColors = resolveSwatches(data, resolver);
    const resolveSourceColors = /* @__PURE__ */ function() {
      let unsubscribes = [];
      return () => {
        const sourceColors = resolveSwatches(
          typographyData.readStable(),
          resolver
        );
        unsubscribes.forEach((u) => u());
        unsubscribes = sourceColors.map(
          (v) => v.subscribe(() => stableValue.reset())
        );
        return sourceColors;
      };
    }();
    const readResolved = () => {
      const sourceColors = resolveSourceColors();
      const resolvedColors = [...sourceColors, ...overrideColors];
      const colors = new Map(
        resolvedColors.map((r) => r.readStable()).filter(isNotNil).map((c) => [c.swatch.id, c])
      );
      const typography = typographyData.readStable();
      const source = typography?.style.map(withColor(colors)) ?? [];
      const override = data?.style.map(withColor(colors)) ?? [];
      return mergeStyles(source, override, stylesheet.breakpoints());
    };
    const stableValue = StableValue({
      name: Definition.type,
      read: () => stylesheet.defineStyle(readResolved()),
      deps: [typographyData, ...overrideColors]
    });
    return {
      ...stableValue,
      triggerResolve: async () => {
        if (typographySub.readStable() == null) {
          typographySub.fetch();
        }
        await Promise.all(
          [
            ...resolveSwatches(typographyData.readStable(), resolver),
            ...overrideColors
          ].map((r) => r.triggerResolve())
        );
      }
    };
  }
  createInstance(sendMessage) {
    return new DefaultControlInstance(sendMessage);
  }
  serialize() {
    return serialize(this.config, {
      type: Definition.type
    });
  }
  introspect(data, target) {
    if (data == null)
      return [];
    if (target.type === Targets.Swatch.type) {
      return data.style.flatMap(
        (typography) => target.introspect(typography.value.color) ?? []
      ) ?? [];
    }
    if (target.type === Targets.Typography.type) {
      return [data.id].filter(isNotNil);
    }
    return [];
  }
  fragmentToData(fragment) {
    if (fragment == null)
      return void 0;
    return {
      id: fragment.id,
      style: fragment.style.map(({ deviceId, value }) => ({
        deviceId,
        value: mapValues(
          value,
          (prop) => prop ?? void 0
        )
      }))
    };
  }
}
function resolveSwatches(data, resolver) {
  return data?.style.flatMap(
    ({ value: { color } }) => color?.swatchId ? [
      Color().resolveSwatch(
        {
          swatchId: color.swatchId,
          alpha: color.alpha ?? 1
        },
        resolver
      )
    ] : []
  ) ?? [];
}
function withColor(colors) {
  return (deviceRawTypographyValue) => {
    const { value, deviceId } = deviceRawTypographyValue;
    if (value.color == null) {
      const { color, ...nextValue } = value;
      return {
        deviceId,
        value: nextValue
      };
    }
    const { swatchId, alpha } = value.color;
    const baseColor = swatchId != null ? colors.get(swatchId) : void 0;
    return {
      deviceId,
      value: {
        ...value,
        color: baseColor != null ? {
          ...baseColor,
          alpha: alpha ?? baseColor?.alpha ?? void 0
        } : void 0
      }
    };
  };
}
class unstable_TypographyDefinition extends Definition {
}
function unstable_Typography() {
  return new unstable_TypographyDefinition();
}
export {
  unstable_Typography,
  unstable_TypographyDefinition
};
//# sourceMappingURL=typography.js.map